import { jsx } from 'react/jsx-runtime';
import { createContext, useState, useRef, useCallback, useMemo } from 'react';
import deepmerge from 'deepmerge';
import { events } from '../events.js';

/**
 * Create Modal History Context.
 */ const ModalHistoryContext = /*#__PURE__*/ createContext({
    history: [],
    push: ()=>null,
    back: ()=>null,
    clear: ()=>null,
    replace: ()=>null
});
/**
 * Create Modal History Provider.
 */ const ModalHistoryProvider = ({ children, registry = {} })=>{
    // Store current history and route of the router
    const [{ route, history }, setState] = useState({
        history: []
    });
    // Store previous route
    const prevRoute = useRef(undefined);
    // Clone state before update, to prevent state mutation
    const setSafeState = useCallback((fn)=>{
        setState(({ route, history })=>{
            // Pass cloned state to the callback function
            return fn({
                route,
                history: [
                    ...history
                ]
            });
        });
    }, []);
    /**
     * Merges next route config with registry config.
     */ const computeRoute = useCallback((nextRoute)=>{
        // Support route as pathname only
        if (typeof nextRoute === 'string') {
            return {
                pathname: nextRoute,
                options: registry?.[nextRoute] ?? {}
            };
        }
        // Support route as `ModalHistory` object
        return {
            pathname: nextRoute.pathname,
            // Merge default options with custom options
            options: deepmerge(registry?.[nextRoute.pathname] ?? {}, nextRoute.options ?? {}),
            query: nextRoute.query
        };
    }, [
        registry
    ]);
    /**
     * Push the next route.
     */ const push = useCallback((nextRoute)=>{
        // Set the next route
        setSafeState(({ route, history })=>{
            // Check for current route
            if (route) {
                // Set previous route in the history
                history.push(route);
            }
            // Compute route
            const computedRoute = computeRoute(nextRoute);
            // Emit route change event
            void events.emit('onRouteChange', {
                prevRoute: route,
                nextRoute: computedRoute
            });
            return {
                history,
                route: computedRoute
            };
        });
    }, [
        setSafeState,
        computeRoute
    ]);
    /**
     * Go back.
     */ const back = useCallback(()=>{
        setSafeState(({ route, history })=>{
            // Pop route
            const popRoute = history.pop();
            // Emit route change event
            void events.emit('onRouteChange', {
                prevRoute: route,
                nextRoute: popRoute
            });
            return {
                // Set the previous route from history
                // as the current route
                route: popRoute,
                // Return the history without the last route
                history
            };
        });
    }, [
        setSafeState
    ]);
    /**
     * Clear history.
     */ const clear = useCallback(()=>{
        // Void current route
        setSafeState(({ route })=>{
            // Store current route as prev route
            // - keep displaying modal content until the modal close
            prevRoute.current = route;
            // Reset route
            return {
                // Clear History
                history: []
            };
        });
    }, [
        setSafeState
    ]);
    /**
     * Replaces the current route with the passed in route. History remains intact.
     */ const replace = useCallback((nextRoute)=>{
        // Set the next route
        setSafeState((currState)=>{
            // Compute route
            const computedRoute = computeRoute(nextRoute);
            // Emit route replace event
            void events.emit('onRouteReplace', {
                prevRoute: currState.route,
                nextRoute: computedRoute
            });
            return {
                ...currState,
                route: computedRoute
            };
        });
    }, [
        computeRoute,
        setSafeState
    ]);
    /**
     * Memorize Provider Value.
     */ const value = useMemo(()=>({
            history,
            prevRoute: prevRoute.current,
            route,
            push,
            back,
            clear,
            replace
        }), [
        history,
        route,
        push,
        back,
        clear,
        replace
    ]);
    return jsx(ModalHistoryContext.Provider, {
        value: value,
        children: children
    });
};

export { ModalHistoryContext, ModalHistoryProvider };
//# sourceMappingURL=History.js.map
